// Pure validation helpers for the first-run onboarding name-capture screen.

/**
 * canProceed returns true when the user has entered at least a non-empty first
 * name (trimmed). The last name is optional. Used to gate the "Next" button and
 * Enter-to-submit on the onboarding screen.
 */
export function canProceed(firstName: string): boolean {
  return firstName.trim().length > 0;
}

/**
 * normalizeName trims surrounding whitespace from a submitted name value.
 */
export function normalizeName(value: string): string {
  return value.trim();
}

/** Number of days the dashboard "Initial Setup" section stays visible after
 * account creation. After this window it is hidden on subsequent logins. */
export const INITIAL_SETUP_WINDOW_DAYS = 14;

/**
 * showInitialSetup decides whether the dashboard "Initial Setup" onboarding
 * section should be shown, based on account age. It returns true only for the
 * first INITIAL_SETUP_WINDOW_DAYS days since account creation (inclusive of day
 * 0, exclusive once the window elapses).
 *
 * Pure + injectable clock so it's unit-testable without a DOM or real time.
 * Defensive: an unparseable/empty createdAt hides the section (returns false)
 * rather than showing it forever.
 *
 * @param createdAt ISO-8601 account creation timestamp (e.g. me.created_at).
 * @param now current time in ms (defaults to Date.now()).
 */
export function showInitialSetup(createdAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (isNaN(created)) return false;
  const windowMs = INITIAL_SETUP_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const age = now - created;
  // Guard against clock skew / future-dated createdAt: treat as within window.
  if (age < 0) return true;
  return age < windowMs;
}

/**
 * runSignupOnboarding sequences the two post-signup steps in the REQUIRED order:
 *   1. capture the name (best-effort — a thrown save must NOT abort the flow),
 *   2. ALWAYS run finishSession afterwards.
 *
 * finishSession is what resumes a pending invite deep-link (reads
 * sessionStorage['vid_pending_invite'] and navigates back to the invite/subscribe
 * page), so it must ALWAYS run — even if the name capture fails. This helper makes
 * that ordering and best-effort contract unit-testable without a DOM.
 *
 * @param captureName runs the name step (may reject; rejection is swallowed).
 * @param finishSession completes login + resumes any pending invite redirect.
 * @returns the ordered list of step names that actually ran, for assertions.
 */
export async function runSignupOnboarding(
  captureName: () => Promise<void>,
  finishSession: () => Promise<void>,
): Promise<string[]> {
  const order: string[] = [];
  try {
    order.push('onboarding');
    await captureName();
  } catch {
    // Best-effort: never trap the user — fall through to finishSession so the
    // invite→subscribe redirect still happens.
  }
  order.push('finishSession');
  await finishSession();
  return order;
}

/**
 * pendingApprovalsLabel returns the headline for the dashboard pending-approvals
 * banner, pluralized. Returns '' for counts <= 0 (banner is hidden in that case).
 */
export function pendingApprovalsLabel(count: number): string {
  if (count <= 0) return '';
  return count === 1
    ? 'You have 1 pending approval'
    : `You have ${count} pending approvals`;
}
